import { useEffect, useMemo, Dispatch, SetStateAction } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import { useAppSelector } from 'app/hooks'
import theme from 'theme_'
import { Secondary } from 'components/Typography'
import { commonMessages } from 'app/i18n/messages'
import {
  CheckoutRowSeparator,
  StyledCheckoutAmounts,
  StyledCheckoutRow,
  StyledCheckoutRowLeft,
  StyledCheckoutRowRight,
  StyledCheckoutRowSecondary,
} from './styles'
import { checkoutAmountMessages } from './messages'

import {
  getInvoiceDiscountAmount,
  getInvoiceDiscountCoupon,
  getInvoiceHasAutoAppliedCoupon,
  formatPrice,
} from '../../utils'
import { upcomingInvoiceSelectors } from '../../selectors'

interface CheckoutAmountsProps {
  currentPrice?: any
  isShowEssentialsPlan: boolean
  setAmountDue: Dispatch<SetStateAction<number>>
}

function CheckoutAmounts({
  isShowEssentialsPlan,
  setAmountDue,
}: CheckoutAmountsProps) {
  const { formatMessage } = useIntl()
  const upcomingInvoice = useAppSelector(upcomingInvoiceSelectors.data)
  const invoiceSubtotal = upcomingInvoice?.subtotal
  const invoiceAmountDue = upcomingInvoice?.amount_due
  const invoiceStartingBalance = upcomingInvoice?.starting_balance
  const invoiceEndingBalance = upcomingInvoice?.ending_balance
  const invoiceTaxes = upcomingInvoice?.total_tax_amounts?.[0]?.amount
  const invoiceDiscount = useMemo(
    () => getInvoiceDiscountAmount(upcomingInvoice),
    [upcomingInvoice]
  )
  const invoiceDiscountCoupon = useMemo(
    () => getInvoiceDiscountCoupon(upcomingInvoice),
    [upcomingInvoice]
  )
  const invoiceHasAutoAppliedCoupon = useMemo(
    () => getInvoiceHasAutoAppliedCoupon(upcomingInvoice),
    [upcomingInvoice]
  )

  // In case of upgrade invoice.lines.data will have 2 items:
  // subscription that will be refunded and subscription to purchase
  const upgradeRefundedSubscription =
    upcomingInvoice?.lines?.data?.length > 1 && upcomingInvoice?.lines.data[0]
  const upgradePurchaseSubscription = upgradeRefundedSubscription
    ? upcomingInvoice?.lines.data[1]
    : undefined

  useEffect(() => {
    if (invoiceAmountDue !== undefined) {
      setAmountDue(invoiceAmountDue)
    }
  }, [invoiceAmountDue, setAmountDue])

  return (
    <StyledCheckoutAmounts>
      {upgradePurchaseSubscription?.amount && (
        <StyledCheckoutRow>
          <StyledCheckoutRowLeft>
            {false && formatMessage(checkoutAmountMessages.subtotal)}
            {upgradePurchaseSubscription?.description}
            {isShowEssentialsPlan &&
              `(${formatMessage(commonMessages.essentialPlan)})`}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            {formatPrice(upgradePurchaseSubscription?.amount)}

            {false &&
              formatPrice(
                upgradePurchaseSubscription
                  ? upgradePurchaseSubscription.amount
                  : invoiceSubtotal
              )}
          </StyledCheckoutRowRight>
        </StyledCheckoutRow>
      )}

      {upgradeRefundedSubscription && (
        <StyledCheckoutRow>
          <StyledCheckoutRowLeft>
            {upgradeRefundedSubscription?.description}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            {formatPrice(upgradeRefundedSubscription.amount)}
          </StyledCheckoutRowRight>
        </StyledCheckoutRow>
      )}

      {invoiceDiscountCoupon && !invoiceHasAutoAppliedCoupon && (
        <StyledCheckoutRow>
          <StyledCheckoutRowLeft>
            {formatMessage(checkoutAmountMessages.discount)}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            -{formatPrice(invoiceDiscount)}
          </StyledCheckoutRowRight>
        </StyledCheckoutRow>
      )}

      {invoiceTaxes && invoiceTaxes !== 0 && (
        <StyledCheckoutRow>
          <StyledCheckoutRowLeft>
            {formatMessage(checkoutAmountMessages.taxes)}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            {formatPrice(invoiceTaxes)}
          </StyledCheckoutRowRight>
        </StyledCheckoutRow>
      )}

      {invoiceStartingBalance < 0 && (
        <StyledCheckoutRow>
          <StyledCheckoutRowLeft>
            {formatMessage(checkoutAmountMessages.startingBalance)}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            {formatPrice(invoiceStartingBalance)}
          </StyledCheckoutRowRight>
        </StyledCheckoutRow>
      )}

      {(upgradeRefundedSubscription ||
        invoiceStartingBalance ||
        invoiceTaxes ||
        invoiceDiscountCoupon ||
        upgradeRefundedSubscription) && <CheckoutRowSeparator />}

      <StyledCheckoutRow sumRow>
        <StyledCheckoutRowLeft>
          {formatMessage(checkoutAmountMessages.amountToPay)}
        </StyledCheckoutRowLeft>
        <StyledCheckoutRowRight>
          {formatPrice(invoiceAmountDue)}
        </StyledCheckoutRowRight>
      </StyledCheckoutRow>

      {invoiceEndingBalance < 0 && (
        <StyledCheckoutRowSecondary>
          <StyledCheckoutRowLeft>
            {formatMessage(checkoutAmountMessages.endingBalance)}
          </StyledCheckoutRowLeft>
          <StyledCheckoutRowRight>
            {formatPrice(invoiceEndingBalance)}
          </StyledCheckoutRowRight>
        </StyledCheckoutRowSecondary>
      )}

      <Secondary color={theme.colors.mutedGray}>
        <FormattedMessage {...checkoutAmountMessages.taxesIncluded} />
      </Secondary>
    </StyledCheckoutAmounts>
  )
}

export default CheckoutAmounts
